import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CigaretteOff, Fuel, Gauge, Palette, PawPrint } from "lucide-react";
import type { CarData } from "@/lib/api";
import { getDistanceUnitPlural } from "@/lib/api";

function mapFuelPolicy(val: unknown, t: (key: string) => string): string {
  const policyMap: Record<string, string> = {
    "1": "Full-to-Full",
    "2": "Included",
    "3": "Same-to-Same",
    "4": "Prepaid",
  };
  const key = String(val);
  return (
    policyMap[key] ||
    (typeof val === "string" && val.length > 1 ? val : t("carDetail.see_listing"))
  );
}

export function ThingsToKnowSection({ car }: { car: CarData }) {
  const { t } = useTranslation();

  // Mileage and fuel policy are gated on rental_driving_active — they
  // only apply when the renter actually drives the car.
  const rows: ReactNode[] = [];

  if (car.pets_allowed != null) {
    rows.push(
      <div key="pets" className="flex items-center gap-3">
        <PawPrint className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-foreground">
          {car.pets_allowed
            ? t("carDetail.pets_allowed")
            : t("carDetail.no_pets")}
        </span>
      </div>,
    );
  }

  // Non-smoking always shows.
  rows.push(
    <div key="non-smoking" className="flex items-center gap-3">
      <CigaretteOff className="h-5 w-5 text-muted-foreground" />
      <span className="text-sm text-foreground">
        {t("carDetail.non_smoking")}
      </span>
    </div>,
  );

  if (car.allow_decorations != null) {
    rows.push(
      <div key="decorations" className="flex items-center gap-3">
        <Palette className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-foreground">
          {car.allow_decorations
            ? t("carDetail.decorations_allowed")
            : t("carDetail.no_decorations")}
        </span>
      </div>,
    );
  }

  if (car.rental_driving_active && car.fuel_policy) {
    rows.push(
      <div key="fuel-policy" className="flex items-center gap-3">
        <Fuel className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-foreground">
          {t("carDetail.fuel_policy", {
            policy: mapFuelPolicy(car.fuel_policy, t),
          })}
        </span>
      </div>,
    );
  }

  if (car.rental_driving_active && car.mileage_limit != null) {
    rows.push(
      <div key="mileage-limit" className="flex items-center gap-3">
        <Gauge className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm text-foreground">
          {car.mileage_limit_miles
            ? t("carDetail.mileage_limit", {
                miles: car.mileage_limit_miles,
                unit: getDistanceUnitPlural(car.country_code),
              })
            : t("carDetail.unlimited_miles", {
                unit: getDistanceUnitPlural(car.country_code),
              })}
        </span>
      </div>,
    );
  }

  if (rows.length === 0) return null;

  return (
    <div className="py-8 border-b border-border">
      <h2 className="text-[22px] font-semibold text-foreground">
        {t("carDetail.things_to_know")}
      </h2>
      <div className="mt-5 grid gap-x-12 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows}
      </div>
    </div>
  );
}
